import { cva } from "class-variance-authority";
import { Eye, Merge } from "lucide-react";
import { motion } from "motion/react";
import type { FocusEvent } from "react";
import { useTranslation } from "react-i18next";

import { getFieldLabel } from "../conditions";
import { SEGMENT_TRANSITION } from "../constants";
import { MergePreview, MergeSummary, type MergeSource } from "./MergePreview";

import { Button } from "@/components/ui/v2/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover";
import type { OverlapGroup, SegmentField } from "@/features/segments/types";

const rowVariants = cva(
  "-mx-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md px-2 py-1.5 transition-colors duration-150 ease-out motion-reduce:transition-none",
  {
    variants: {
      highlighted: {
        true: "[background:var(--badge-info-bg)]",
        false: "",
      },
    },
    defaultVariants: { highlighted: false },
  },
);

type OverlapGroupRowProps = {
  group: OverlapGroup;
  fields: SegmentField[];
  sources: MergeSource[];
  isHighlighted: boolean;
  onMerge: () => void;
  onHighlightChange: (isHighlighted: boolean) => void;
};

export function OverlapGroupRow({
  group,
  fields,
  sources,
  isHighlighted,
  onMerge,
  onHighlightChange,
}: OverlapGroupRowProps) {
  const { t } = useTranslation();
  const label = getFieldLabel(fields, group.field);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    onHighlightChange(false);
  };

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={SEGMENT_TRANSITION}
      data-testid="overlap-group"
      data-field={group.field}
      data-highlighted={isHighlighted}
      className={rowVariants({ highlighted: isHighlighted })}
      onPointerEnter={() => onHighlightChange(true)}
      onPointerLeave={() => onHighlightChange(false)}
      onFocus={() => onHighlightChange(true)}
      onBlur={handleBlur}
    >
      <p className="flex min-w-0 items-center gap-2 text-sm">
        <Merge
          aria-hidden
          className="size-4 shrink-0 [color:var(--badge-info-text)]"
        />
        <MergeSummary group={group} sources={sources} fields={fields} />
      </p>
      <div className="flex items-center gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={t("admin.segments.builder.overlap.previewLabel", {
                field: label,
              })}
              data-testid="overlap-group-preview"
            >
              <Eye />
              {t("admin.segments.builder.overlap.preview")}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-96 max-w-[calc(100vw-2rem)]"
            data-testid="overlap-preview"
          >
            <MergePreview group={group} sources={sources} fields={fields} />
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={t("admin.segments.builder.overlap.mergeLabel", {
            field: label,
          })}
          onClick={onMerge}
          data-testid="overlap-group-merge"
        >
          <Merge />
          {t("admin.segments.builder.overlap.merge")}
        </Button>
      </div>
    </motion.div>
  );
}
